import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import styles from './AboutUs.module.css';

const AboutUs = () => {
  const [ref, inView] = useInView({ 
    triggerOnce: true, 
    threshold: 0.2 
  });

  const stats = [
    { value: 7, suffix: 'min', label: 'Loan Approval' },
    { value: 92, suffix: '%', label: 'ML Accuracy' },
    { value: 150, suffix: '+', label: 'Data Points' },
    { value: 24, suffix: '/7', label: 'Availability' }
  ];

  const features = [
    {
      tag: 'SVE',
      title: 'Share Valuation Engine',
      description: 'Real-time valuation of institutional shares using hybrid ML models',
      metrics: ['Real-time', '95%+ Accuracy']
    },
    {
      tag: 'CSE',
      title: 'Credit Scoring Engine',
      description: 'AI-powered assessment with 150+ data points and 92% accuracy',
      metrics: ['<7 min', '92% Accuracy']
    }
  ];

  return (
    <section className={styles.section} id="about" data-section="about" ref={ref}>
      <div className={styles.container}>
        
        {/* Header */}
        <motion.div 
          className={styles.header}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className={styles.tagline}>About Scorex</div>
          <h2 className={styles.title}>
            AI-Powered Credit Intelligence for African Markets
          </h2>
          <p className={styles.description}>
            We deliver unified AI solutions that transform credit risk management—enabling financial institutions to make faster, smarter, and more inclusive lending decisions across Africa.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div 
          className={styles.statsRow}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {stats.map((stat, index) => (
            <div key={index} className={styles.statBox}>
              <Counter 
                end={stat.value} 
                suffix={stat.suffix} 
                inView={inView}
                duration={2}
              />
              <div className={styles.statLabel}>{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <div className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className={styles.featureCard}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              <div className={styles.featureHeader}>
                <span className={styles.featureTag}>{feature.tag}</span>
                <div className={styles.featureMetrics}>
                  {feature.metrics.map((metric, i) => (
                    <span key={i} className={styles.metric}>{metric}</span>
                  ))}
                </div>
              </div>
              <h3 className={styles.featureTitle}>{feature.title}</h3>
              <p className={styles.featureDescription}>{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Mission Statement */}
        <motion.div 
          className={styles.mission}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className={styles.missionContent}>
            <h3 className={styles.missionTitle}>Our Mission</h3>
            <p className={styles.missionText}>
              To empower African financial institutions with transparent, intelligent AI solutions that enable sustainable and inclusive lending practices
            </p>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

// Counter Component with Animation
const Counter = ({ end, suffix, inView, duration }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = (timestamp - startTime) / (duration * 1000);

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [inView, end, duration]);

  return (
    <div className={styles.counterWrapper}>
      <span className={styles.counterValue}>{count}</span>
      <span className={styles.counterSuffix}>{suffix}</span>
    </div>
  );
};

export default AboutUs;